import React__default from 'react';
import PT from 'prop-types';
import styled from 'styled-components';
import { I as Icon } from '../Icon/Icon-CpzUnHit.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { t as times } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-EM4gOTGb-B7bhIt-r.js';
import { w as withTaktFallbackId } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { T as TextButton } from '../Button/TextButton/TextButton-DBMcje7x.js';

const TooltipContent = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-0"
})(["padding:", " ", ";overflow:visible;max-width:", ";max-height:", ";"], ({
  theme
}) => theme.tooltip.paddingTopBottom, ({
  theme
}) => theme.tooltip.paddingLeftRight, ({
  theme
}) => theme.tooltip.maxWidth, ({
  theme
}) => theme.tooltip.maxHeight);
TooltipContent.displayName = 'TooltipContent';
const TooltipContentWithTopCornerIcon = /*#__PURE__*/styled(TooltipContent).withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-1"
})(["padding:", " ", ";"], ({
  theme
}) => theme.tooltip.paddingTopBottomWithTopCornerIcon, ({
  theme
}) => theme.tooltip.paddingLeftRightWithTopCornerIcon);
TooltipContentWithTopCornerIcon.displayName = 'AnchoredAlertContentWithTopCornerIcon';
const TooltipScrollContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-2"
})(["overflow-x:auto;"]);
TooltipScrollContainer.displayName = 'TooltipScrollContainer';
const TooltipMessage = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-3"
})(["", ";"], ({
  theme
}) => theme.typography.base);
TooltipMessage.displayName = 'TooltipMessage';
const CloseButtonBase = /*#__PURE__*/styled(TextButton).withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-4"
})(["opacity:", ";padding:", ";text-align:center;color:", ";outline:none;margin-inline-start:", ";:hover{opacity:1;background:", ";color:", ";}@media (prefers-reduced-motion:no-preference){transition:background .2s ease-in-out;}:focus{opacity:1;}"], ({
  $styleType
}) => $styleType === 'blue' || $styleType === 'dark' ? 1 : 0.5, ({
  theme
}) => theme.tooltip.closeIcon.padding, ({
  $styleType,
  theme
}) => theme.tooltip.closeIcon[$styleType], ({
  theme
}) => theme.tooltip.closeIcon.marginLeft, ({
  $styleType,
  theme
}) => theme.tooltip.closeIconBg[$styleType], ({
  $styleType,
  theme
}) => theme.tooltip.closeIcon[$styleType]);
const CloseButton = withTaktFallbackId(CloseButtonBase);
const NoMarginCloseIcon = /*#__PURE__*/styled(CloseButton).withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-5"
})(["margin:0;margin-inline-start:auto;"]);
const NoMarginCloseButton = withTaktFallbackId(NoMarginCloseIcon);
const CloseIcon = /*#__PURE__*/styled(Icon).withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-6"
})(["line-height:1;margin:0;padding:0;display:inline-block;.svg-inline--fa{vertical-align:", ";width:", ";}"], ({
  theme
}) => theme.tooltip.closeIcon.svg.verticalAlign, ({
  theme
}) => theme.tooltip.closeIcon.svg.width);
CloseIcon.displayName = 'CloseIcon';
const FlexContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-7"
})(["display:flex;align-items:flex-start;"]);
const ContentContainerWithTopCornerIcon = /*#__PURE__*/styled(FlexContainer).withConfig({
  componentId: "sc-storm-ui-30060407__sc-skh689-8"
})(["padding:", " ", ";"], ({
  theme
}) => theme.tooltip.contentPaddingTopBottomWithTopCornerIcon, ({
  theme
}) => theme.tooltip.contentPaddingLeftRightWithTopCornerIcon);
ContentContainerWithTopCornerIcon.displayName = 'ContentContainerWithTopCornerIcon';
const TooltipContentContainer = ({
  children,
  closeButtonLabel,
  handleCloseClicked,
  footer,
  message,
  messageId,
  type = 'light',
  withCloseButton,
  closeIconPosition
}) => {
  const DefaultLayout = /*#__PURE__*/React__default.createElement(TooltipContent, null, /*#__PURE__*/React__default.createElement(FlexContainer, null, /*#__PURE__*/React__default.createElement("div", {
    id: messageId
  }, message ? /*#__PURE__*/React__default.createElement(TooltipMessage, null, message) : children), withCloseButton && /*#__PURE__*/React__default.createElement(CloseButton, {
    "aria-label": closeButtonLabel,
    onClick: handleCloseClicked,
    $styleType: type,
    type: "button",
    taktFallbackId: createStormTaktId('close-button')
  }, /*#__PURE__*/React__default.createElement(CloseIcon, {
    type: times
  }))), footer && /*#__PURE__*/React__default.createElement(FlexContainer, null, footer));
  const TopCornerCloseIconLayout = /*#__PURE__*/React__default.createElement(TooltipContentWithTopCornerIcon, null, /*#__PURE__*/React__default.createElement(FlexContainer, null, /*#__PURE__*/React__default.createElement(NoMarginCloseButton, {
    "aria-label": closeButtonLabel,
    onClick: handleCloseClicked,
    $styleType: type,
    type: "button",
    taktFallbackId: createStormTaktId('close-button')
  }, /*#__PURE__*/React__default.createElement(CloseIcon, {
    type: times
  }))), /*#__PURE__*/React__default.createElement(ContentContainerWithTopCornerIcon, null, /*#__PURE__*/React__default.createElement("div", null, message ? /*#__PURE__*/React__default.createElement(TooltipMessage, null, message) : children)), footer && /*#__PURE__*/React__default.createElement(FlexContainer, null, footer));
  return /*#__PURE__*/React__default.createElement(TooltipScrollContainer, null, closeIconPosition === 'topCorner' ? TopCornerCloseIconLayout : DefaultLayout);
};
TooltipContentContainer.propTypes = {
  handleCloseClicked: PT.func.isRequired,
  children: PT.node,
  closeButtonLabel: PT.string,
  footer: PT.node,
  message: PT.node,
  type: PT.oneOf(['light', 'dark', 'blue']),
  withCloseButton: PT.bool,
  closeIconPosition: PT.oneOf(['default', 'topCorner'])
};
TooltipContentContainer.defaultProps = {
  children: undefined,
  closeButtonLabel: 'Close Tooltip',
  footer: undefined,
  message: '',
  messageId: undefined,
  type: 'light',
  withCloseButton: true,
  closeIconPosition: 'default'
};

export { TooltipContentContainer as T };
//# sourceMappingURL=TooltipContent-B7HoG4tK.js.map
